import {
	clearEnvironmentSharedData,
	ClearSharedDataForEnvironmentRequestModel
} from "client/Api/InstanceOperatorPage/instanceOperatorPage";
import {
	Checkbox,
	Dialog,
	Form,
	FormattedText,
	FormItem,
	Toolbar
} from "client/Components";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import {
	ReactElement,
	useCallback,
	useState
} from "react";

export type ClearEnvironmentCacheDialogProps = {
	environmentName: string;
	environmentId: Guid;
	onClose: () => void;
};

export const ClearEnvironmentCacheDialog = (props: ClearEnvironmentCacheDialogProps): ReactElement => {
	const {
		environmentName,
		environmentId,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormData>({
		cache: true,
		temporaryData: false
	});

	const alertManager: AlertManager = useAlerts();

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				const model: ClearSharedDataForEnvironmentRequestModel = {
					...formData,
					environmentId
				};

				await clearEnvironmentSharedData(model);
				onClose();
			} catch (e) {
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			environmentId,
			formData,
			isBusy,
			onClose
		]
	);

	return (
		<Dialog hasBusyIndicator={isBusy}>
			<Dialog.Header
				title={(
					<FormattedText
						text="Which shared data do you want to clear on environment '{{Name}}'?"
						thingsToReplace={[
							{ what: "{{Name}}", filler: environmentName }
						]}
					/>
				)}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem>
						<NoUniqueInputIdContext>
							<Checkbox
								{...formHandler.for("cache").attachToCheckbox()}
								text="Cache"
							/>

							<Checkbox
								{...formHandler.for("temporaryData").attachToCheckbox()}
								text="Temporary data - This will DELETE the temporary data permanently"
								icon={{
									kind: "font",
									name: "triangle-exclamation",
									color: "danger"
								}}
							/>
						</NoUniqueInputIdContext>
					</FormItem>
				</Form>

			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						text="Clear"
						kind="primary"
						onClick={submitHandler}
					/>

					<Toolbar.Button
						text="Cancel"
						kind="secondary"
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ClearEnvironmentCacheDialog.displayName = "ClearEnvironmentCacheDialog";

type FormData = Omit<ClearSharedDataForEnvironmentRequestModel, "environmentId">;

export type ClearEnvironmentCacheDialogInitialData = Omit<ClearEnvironmentCacheDialogProps, "onClose">;

export function useClearEnvironmentCacheDialog(): [
	element: ReactElement | null,
	open: (props: ClearEnvironmentCacheDialogInitialData) => Promise<void>
] {
	const [element, originalOpen] = useDataInputDialog<ClearEnvironmentCacheDialogInitialData, void>(
		(props, onClose) => (
			<ClearEnvironmentCacheDialog
				{...props}
				onClose={onClose}
			/>
		)
	);

	const open = useCallback(
		async (props: ClearEnvironmentCacheDialogInitialData): Promise<void> => {
			await originalOpen(props);
		},
		[originalOpen]
	);

	return [element, open];
}
